import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { 
    FindAllIndustriesAdminQueryQuery, 
    FindAllIndustriesAdminQueryQueryVariables 
} from "../../../../schemaTypes";
import { useCallback } from "react";

export const findAllIndustriesAdminQuery = gql`
  query FindAllIndustriesAdminQuery(
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllIndustriesAdmin(
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
   		  id
        name
        createdAt
        isArchive
        partnerCount
        createdBy{
            id
            admin{
              id
              firstname
              lastname
              email
              level
            }
        }
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllIndustriesAdmin {
  data: FindAllIndustriesAdminQueryQuery["findAllIndustriesAdmin"]["data"]
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreIndustries: boolean;
}

interface Props {
  variables: FindAllIndustriesAdminQueryQueryVariables;
  children: (data: WithFindAllIndustriesAdmin) => React.ReactElement | null;
}

export const FindAllIndustriesAdminController: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllIndustriesAdminQueryQuery,
    FindAllIndustriesAdminQueryQueryVariables
  >(findAllIndustriesAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const findAllIndustriesAdminData = data?.findAllIndustriesAdmin?.data || [];
  const page = data?.findAllIndustriesAdmin?.page || variables.page;
  const pageSize = data?.findAllIndustriesAdmin?.pageSize || variables.pageSize;
  const total = data?.findAllIndustriesAdmin?.total || 0;
  const totalPages = data?.findAllIndustriesAdmin?.totalPages || 0;
  const hasMoreIndustries = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreIndustries) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findAllIndustriesAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllIndustriesAdmin: {
            ...fetchMoreResult?.findAllIndustriesAdmin,
            data: [
              ...(prev?.findAllIndustriesAdmin?.data || []),
              ...(fetchMoreResult?.findAllIndustriesAdmin?.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreIndustries]);

  return children({
    data: findAllIndustriesAdminData as any,
    loading,
    loadMore,
    hasMoreIndustries,
    page,
    pageSize,
    total,
    totalPages,
  });
};